import React from 'react';
import { Server, Shield, AtSign, Key, Send } from 'lucide-react';
import EmailFormField from './EmailFormField';

/**
 * Provider preset row + SMTP field grid + provider-specific help hints.
 * Extracted from EmailMailboxSection (Vague 97.3).
 *
 * Stateless — the parent owns the form state and provides setForm callbacks.
 */
export default function EmailSmtpForm({ providers, form, setForm, applyPreset, hasExisting, onTestImap, testingImap, testImapResult }) {
  return (
    <div data-testid="email-smtp-form">
      {/* Provider presets */}
      <div className="mb-4">
        <label className="text-[12px] font-semibold text-[#4A4B63] block mb-2">Fournisseur</label>
        <div className="flex flex-wrap gap-2">
          {providers.map((p) => (
            <button
              key={p.id}
              type="button"
              data-testid={`email-preset-${p.id}`}
              onClick={() => applyPreset(p.id)}
              className={`h-9 px-3 rounded-lg border text-[12px] font-semibold capitalize flex items-center gap-1.5 ${form.provider === p.id ? 'border-[#4F46E5] bg-[#EEF2FF] text-[#4F46E5]' : 'border-[#EFF0F6] text-[#4A4B63] hover:border-[#C7D2FE]'}`}
            >
              <Server className="w-3.5 h-3.5" /> {p.id}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setForm((s) => ({ ...s, provider: 'custom' }))}
            className={`h-9 px-3 rounded-lg border text-[12px] font-semibold ${form.provider === 'custom' ? 'border-[#4F46E5] bg-[#EEF2FF] text-[#4F46E5]' : 'border-[#EFF0F6] text-[#4A4B63]'}`}
          >
            Personnalisé
          </button>
        </div>
      </div>

      {/* SMTP fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <EmailFormField icon={Server} label="Serveur SMTP" placeholder="smtp.gmail.com" value={form.host} onChange={(v) => setForm((s) => ({ ...s, host: v }))} testId="email-host" />
        <EmailFormField icon={Shield} label="Port" type="number" placeholder="587" value={form.port} onChange={(v) => setForm((s) => ({ ...s, port: v }))} testId="email-port" />
        <div>
          <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Sécurité</label>
          <select
            value={form.security}
            onChange={(e) => setForm((s) => ({ ...s, security: e.target.value }))}
            data-testid="email-security"
            className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px]"
          >
            <option value="starttls">STARTTLS (587)</option>
            <option value="ssl">SSL/TLS (465)</option>
            <option value="none">Aucune (25)</option>
          </select>
        </div>
        <EmailFormField icon={AtSign} label="Nom d'utilisateur" placeholder="votre-email@gmail.com" value={form.username} onChange={(v) => setForm((s) => ({ ...s, username: v }))} testId="email-username" />
        <EmailFormField
          icon={Key}
          label={hasExisting ? 'Mot de passe (laisser vide pour garder)' : "Mot de passe / mot de passe d'application"}
          type="password"
          value={form.password}
          onChange={(v) => setForm((s) => ({ ...s, password: v }))}
          testId="email-password"
        />
        <EmailFormField icon={Send} label="Email d'expédition" placeholder="votre-email@gmail.com" value={form.from_email} onChange={(v) => setForm((s) => ({ ...s, from_email: v }))} testId="email-from-email" />
        <EmailFormField icon={AtSign} label="Nom d'expédition" placeholder="OFManager" value={form.from_name} onChange={(v) => setForm((s) => ({ ...s, from_name: v }))} testId="email-from-name" full />
      </div>

      {/* Phase 3 — IMAP read settings (optional, auto-filled from preset) */}
      <details className="mt-3 group" data-testid="email-imap-details">
        <summary className="cursor-pointer text-[12px] font-semibold text-[#4F46E5] hover:underline">
          Avancé · Réglages IMAP (lecture des emails dans OFManager)
        </summary>
        <div className="mt-2 p-3 rounded-lg bg-[#FAFAFE] border border-[#EFF0F6] grid grid-cols-1 sm:grid-cols-2 gap-3">
          <EmailFormField
            icon={Server} label="Serveur IMAP" placeholder="imap.gmail.com"
            value={form.imap_host || ''} onChange={(v) => setForm((s) => ({ ...s, imap_host: v }))}
            testId="email-imap-host"
          />
          <EmailFormField
            icon={Shield} label="Port IMAP" type="number" placeholder="993"
            value={form.imap_port || ''} onChange={(v) => setForm((s) => ({ ...s, imap_port: v }))}
            testId="email-imap-port"
          />
          <div>
            <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Sécurité IMAP</label>
            <select
              value={form.imap_security || 'ssl'}
              onChange={(e) => setForm((s) => ({ ...s, imap_security: e.target.value }))}
              data-testid="email-imap-security"
              className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px]"
            >
              <option value="ssl">SSL/TLS (993)</option>
              <option value="starttls">STARTTLS (143)</option>
              <option value="none">Aucune (143)</option>
            </select>
          </div>
          <div className="text-[11px] text-[#6B6C85] self-end pb-1">
            Laissez vide si vous voulez seulement <em>envoyer</em> des emails (SMTP).
            Renseigné = vos emails reçus apparaîtront dans <strong>Boîte email</strong>.
          </div>
          {onTestImap && (
            <div className="sm:col-span-2">
              <button
                type="button"
                onClick={onTestImap}
                disabled={testingImap}
                data-testid="email-imap-test-btn"
                className="h-9 px-3 rounded-lg border border-[#4F46E5] text-[#4F46E5] hover:bg-[#EEF2FF] text-[12px] font-semibold inline-flex items-center gap-2 disabled:opacity-50"
              >
                {testingImap ? 'Test en cours…' : 'Tester la connexion IMAP'}
              </button>
              {testImapResult && (
                <div
                  data-testid="email-imap-test-result"
                  className={`mt-2 px-3 py-2 rounded-md text-[12px] ${
                    testImapResult.ok
                      ? 'bg-[#ECFDF5] text-[#065F46] border border-[#A7F3D0]'
                      : 'bg-[#FEF2F2] text-[#B91C1C] border border-[#FECACA]'
                  }`}
                >
                  {testImapResult.ok
                    ? `✓ Connexion réussie sur ${testImapResult.host}:${testImapResult.port}`
                    : `✗ Échec (${testImapResult.step}) — ${testImapResult.detail}`}
                </div>
              )}
            </div>
          )}
        </div>
      </details>

      {/* Provider-specific help */}
      {form.provider === 'gmail' && (
        <div className="mt-3 p-3 rounded-lg bg-[#EFF6FF] border border-[#BFDBFE] text-[11.5px] text-[#1E40AF]">
          <strong>Gmail :</strong> activez la « <a className="underline" href="https://myaccount.google.com/security" target="_blank" rel="noreferrer">validation en 2 étapes</a> » puis créez un « <a className="underline" href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer">mot de passe d'application</a> » de 16 caractères à coller ici.
        </div>
      )}
      {form.provider === 'outlook' && (
        <div className="mt-3 p-3 rounded-lg bg-[#EFF6FF] border border-[#BFDBFE] text-[11.5px] text-[#1E40AF]">
          <strong>Outlook/Microsoft 365 :</strong> générez un mot de passe d'application depuis votre compte Microsoft si la validation en 2 étapes est active.
        </div>
      )}
    </div>
  );
}
